import React, { useRef, useEffect } from "react";
import "./ParallaxSection.css";

const ParallaxSection = ({ imageUrl, children, className = "" }) => {
  const parallaxRef = useRef(null);

  // Build Cloudinary URL if needed
  const getImageUrl = (src) => {
    if (!src) return null;

    // If already a full URL, use as-is
    if (src.startsWith("http")) return src;

    // Build Cloudinary URL for public ID
    const cloudName = process.env.REACT_APP_CLOUDINARY_NAME;
    if (!cloudName) {
      console.warn("REACT_APP_CLOUDINARY_NAME not set, using src as-is");
      return src;
    }

    const publicId = src.startsWith("/") ? src.substring(1) : src;
    return `https://res.cloudinary.com/${cloudName}/image/upload/f_jpg,q_auto/${publicId}`;
  };

  // Parallax scroll effect with throttling for better performance
  useEffect(() => {
    let ticking = false;

    // Check if user prefers reduced motion
    const prefersReducedMotion = window.matchMedia(
      "(prefers-reduced-motion: reduce)"
    ).matches;

    const handleScroll = () => {
      if (!ticking && !prefersReducedMotion) {
        requestAnimationFrame(() => {
          if (parallaxRef.current) {
            const rect = parallaxRef.current.getBoundingClientRect();
            const isVisible = rect.top < window.innerHeight && rect.bottom > 0;

            if (isVisible) {
              const scrolled = window.pageYOffset;
              const parallaxElement = parallaxRef.current.querySelector(
                ".parallax-background"
              );
              if (parallaxElement) {
                const sectionTop =
                  parallaxRef.current.getBoundingClientRect().top +
                  window.pageYOffset;
                const relativeScroll = scrolled - sectionTop;
                const speed = 0.4; // Increased for more dramatic movement
                parallaxElement.style.transform = `translateY(${
                  relativeScroll * speed
                }px)`;
              }
            }
          }
          ticking = false;
        });
        ticking = true;
      }
    };

    // Set background image via JavaScript to avoid CSS path issues
    if (parallaxRef.current && imageUrl) {
      const parallaxElement = parallaxRef.current.querySelector(
        ".parallax-background"
      );
      if (parallaxElement) {
        const finalImageUrl = getImageUrl(imageUrl);
        parallaxElement.style.backgroundImage = `url('${finalImageUrl}')`;
      }
    }

    // Intersection Observer for animation trigger
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("animate-in");
          }
        });
      },
      { threshold: 0.3 }
    );

    if (parallaxRef.current) {
      observer.observe(parallaxRef.current);
    }

    window.addEventListener("scroll", handleScroll, { passive: true });

    return () => {
      window.removeEventListener("scroll", handleScroll);
      observer.disconnect();
    };
  }, [imageUrl]);

  return (
    <div className={`parallax-section ${className}`} ref={parallaxRef}>
      <div className="parallax-background"></div>
      <div className="parallax-content">
        <div className="parallax-vignette"></div>
        <div className="parallax-overlay">
          <div className="content-container">
            <div className="text-center py-32">{children}</div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ParallaxSection;
